import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

const STORAGE_KEY = 'zynka-theme-mode';
const ThemeModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setModePreference: () => {},
  prefersDark: false
});

const getStoredMode = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === 'dark' || saved === 'light' ? saved : null;
};

const getSystemMode = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export function ThemeModeProvider({ children }) {
  const [hasManualSelection, setHasManualSelection] = useState(() => Boolean(getStoredMode()));
  const [mode, setMode] = useState(() => getStoredMode() || getSystemMode());
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  useEffect(() => {
    if (!hasManualSelection) {
      setMode(prefersDark ? 'dark' : 'light');
    }
  }, [prefersDark, hasManualSelection]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.colorMode = mode;
      document.documentElement.style.colorScheme = mode;
    }
    if (typeof window !== 'undefined') {
      if (hasManualSelection) {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [mode, hasManualSelection]);

  const setModePreference = useCallback((nextMode) => {
    setHasManualSelection(true);
    setMode(nextMode === 'dark' ? 'dark' : 'light');
  }, []);

  const toggleMode = useCallback(() => {
    setModePreference(mode === 'light' ? 'dark' : 'light');
  }, [mode, setModePreference]);

  const value = useMemo(
    () => ({ mode, toggleMode, setModePreference, prefersDark }),
    [mode, toggleMode, setModePreference, prefersDark]
  );

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

ThemeModeProvider.propTypes = {
  children: PropTypes.node
};

export const useThemeMode = () => useContext(ThemeModeContext);
